import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { authApi } from '../services/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eeacbd60-db8b-4da1-8e71-db72803ff0fd/artifacts/f4528czt_Screenshot%202024-07-25%20103325.png";

export default function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await authApi.requestPasswordReset(email);
      setSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading mb-3">
            Check Your Email
          </h1>
          <p className="text-slate-600 mb-6">
            If an account exists with <strong>{email}</strong>, you will receive password reset instructions shortly.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Note: For security reasons, password resets are handled by your administrator. Please contact your EYB administrator if you don't receive an email.
          </p>
          <Link to="/login">
            <Button variant="outline" className="flex items-center gap-2 mx-auto">
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="EYB" className="h-12 w-12" />
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">
            Reset Password
          </h1>
          <p className="text-slate-600">
            Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="you@eybconsulting.com"
                className="pl-10 h-12"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="reset-email"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full btn-primary h-12"
            disabled={loading}
            data-testid="reset-submit"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Send Reset Instructions'
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-sky-600 font-medium hover:text-sky-700 flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
